import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Loader2, Bed, DoorOpen, CheckCircle, XCircle, Layers, 
  RotateCcw, AlertCircle, Image
} from 'lucide-react';
import { toast } from 'sonner';

export default function RoomAmenitiesPage() {
  const [roomTypes, setRoomTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomTypeAmenities, setRoomTypeAmenities] = useState([]);
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [roomTypeDialogOpen, setRoomTypeDialogOpen] = useState(false);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [savingIds, setSavingIds] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [roomTypesRes, roomsRes] = await Promise.all([
        api.get('/room-types'),
        api.get('/rooms')
      ]);
      setRoomTypes(roomTypesRes.data || []);
      setRooms(roomsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const openRoomTypeDialog = async (roomType) => {
    setSelectedRoomType(roomType);
    setRoomTypeDialogOpen(true);
    
    try {
      const response = await api.get(`/amenity-management/room-types/${roomType.id}`);
      setRoomTypeAmenities(response.data);
    } catch (error) {
      toast.error('Failed to fetch room type amenities');
      setRoomTypeAmenities([]);
    }
  };

  const openRoomDialog = async (room) => {
    setSelectedRoom(room);
    setRoomDialogOpen(true);
    
    try {
      const response = await api.get(`/amenity-management/rooms/${room.id}`);
      setRoomAmenities(response.data);
    } catch (error) {
      toast.error('Failed to fetch room amenities');
      setRoomAmenities([]);
    }
  };

  const toggleRoomTypeAmenity = async (amenityId, currentStatus) => {
    setSavingIds(prev => new Set(prev).add(amenityId));
    try {
      await api.put(`/amenity-management/room-types/${selectedRoomType.id}`, {
        amenity_ids: [amenityId],
        is_active: !currentStatus
      });
      
      setRoomTypeAmenities(prev => 
        prev.map(a => a.id === amenityId ? { ...a, is_active: !currentStatus } : a)
      );
      
      toast.success(`Amenity ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update amenity');
    } finally {
      setSavingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(amenityId);
        return newSet;
      });
    }
  };

  const toggleRoomAmenity = async (amenityId, currentStatus, isOverride) => {
    setSavingIds(prev => new Set(prev).add(amenityId));
    try {
      await api.put(`/amenity-management/rooms/${selectedRoom.id}`, {
        amenity_ids: [amenityId],
        is_active: !currentStatus
      });
      
      setRoomAmenities(prev => 
        prev.map(a => a.id === amenityId ? { ...a, is_active: !currentStatus, is_override: true } : a)
      );
      
      toast.success('Room amenity updated');
    } catch (error) {
      toast.error('Failed to update amenity');
    } finally {
      setSavingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(amenityId);
        return newSet;
      });
    }
  };

  const removeRoomOverride = async (amenityId) => {
    setSavingIds(prev => new Set(prev).add(amenityId));
    try {
      await api.delete(`/amenity-management/rooms/${selectedRoom.id}/${amenityId}`);
      
      // Refresh to get the correct room type status
      const response = await api.get(`/amenity-management/rooms/${selectedRoom.id}`);
      setRoomAmenities(response.data);
      
      toast.success('Override removed - using room type setting');
    } catch (error) {
      toast.error('Failed to remove override');
    } finally {
      setSavingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(amenityId);
        return newSet;
      });
    }
  };

  const toggleAllRoomTypeAmenities = async (activate) => {
    try {
      const amenityIds = roomTypeAmenities.map(a => a.id);
      await api.put(`/amenity-management/room-types/${selectedRoomType.id}`, {
        amenity_ids: amenityIds,
        is_active: activate
      });
      
      setRoomTypeAmenities(prev => prev.map(a => ({ ...a, is_active: activate })));
      toast.success(`All amenities ${activate ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update amenities');
    }
  };

  const getRoomsForType = (roomTypeId) => {
    return rooms.filter(r => r.room_type_id === roomTypeId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Layers className="h-6 w-6 text-indigo-600" />
          Room Amenities
        </h1>
        <p className="text-gray-500 mt-1">
          Manage amenities for room types and individual rooms
        </p>
      </div>

      <Tabs defaultValue="room-types">
        <TabsList className="mb-6">
          <TabsTrigger value="room-types" className="flex items-center gap-2">
            <Bed className="h-4 w-4" />
            Room Types
          </TabsTrigger>
          <TabsTrigger value="rooms" className="flex items-center gap-2">
            <DoorOpen className="h-4 w-4" />
            Individual Rooms
          </TabsTrigger>
        </TabsList>

        <TabsContent value="room-types">
          <Card>
            <CardHeader>
              <CardTitle>Room Type Amenities</CardTitle>
              <CardDescription>
                Set default amenities for each room type. All rooms of this type will inherit these settings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {roomTypes.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No room types found</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {roomTypes.map(roomType => (
                    <div
                      key={roomType.id}
                      className="p-4 border rounded-lg hover:border-indigo-300 hover:bg-indigo-50/50 cursor-pointer transition-colors"
                      onClick={() => openRoomTypeDialog(roomType)}
                      data-testid={`room-type-card-${roomType.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <Bed className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{roomType.name}</p>
                          <p className="text-sm text-gray-500">
                            {getRoomsForType(roomType.id).length} room(s) • ₹{roomType.base_rate}/night
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rooms">
          <Card>
            <CardHeader>
              <CardTitle>Individual Room Amenities</CardTitle>
              <CardDescription>
                Override room type settings for specific rooms. Changes here take precedence over room type defaults.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Room Type Filter */}
              <div className="mb-6">
                <Select onValueChange={(value) => setSelectedRoomType(roomTypes.find(rt => rt.id === value))}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Filter by Room Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Room Types</SelectItem>
                    {roomTypes.map(rt => (
                      <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {rooms.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No rooms found</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {rooms
                    .filter(room => !selectedRoomType || room.room_type_id === selectedRoomType.id)
                    .map(room => {
                      const roomType = roomTypes.find(rt => rt.id === room.room_type_id);
                      return (
                        <div
                          key={room.id}
                          className="p-4 border rounded-lg hover:border-indigo-300 hover:bg-indigo-50/50 cursor-pointer transition-colors"
                          onClick={() => openRoomDialog(room)}
                          data-testid={`room-card-${room.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <DoorOpen className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">Room {room.room_number}</p>
                              <p className="text-xs text-gray-500">{roomType?.name || 'Unknown type'}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Room Type Amenities Dialog */}
      <Dialog open={roomTypeDialogOpen} onOpenChange={setRoomTypeDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bed className="h-5 w-5" />
              {selectedRoomType?.name} - Amenities
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-500">
                These amenities apply to all rooms of this type
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAllRoomTypeAmenities(true)}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Activate All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAllRoomTypeAmenities(false)}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Deactivate All
                </Button>
              </div>
            </div>
            
            <div className="max-h-96 overflow-y-auto space-y-2">
              {roomTypeAmenities.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No amenities available for this hotel.</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Ask your company admin to activate amenities for this hotel first.
                  </p>
                </div>
              ) : (
                roomTypeAmenities.map(amenity => (
                  <div
                    key={amenity.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      amenity.is_active ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded border flex items-center justify-center overflow-hidden">
                        {amenity.icon_url ? (
                          <img src={amenity.icon_url} alt="" className="w-6 h-6 object-contain" />
                        ) : (
                          <Image className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{amenity.name}</p>
                        <p className="text-xs text-gray-500">{amenity.group?.name || 'No group'}</p>
                      </div>
                    </div>
                    <Switch
                      checked={amenity.is_active}
                      onCheckedChange={() => toggleRoomTypeAmenity(amenity.id, amenity.is_active)}
                      disabled={savingIds.has(amenity.id)}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Room Amenities Dialog */}
      <Dialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DoorOpen className="h-5 w-5" />
              Room {selectedRoom?.room_number} - Amenities
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Changes here override room type defaults. 
                Use the reset button to revert to room type settings.
              </p>
            </div>
            
            <div className="max-h-96 overflow-y-auto space-y-2">
              {roomAmenities.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No amenities available.</p>
                </div>
              ) : (
                roomAmenities.map(amenity => (
                  <div
                    key={amenity.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      amenity.is_active ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    } ${amenity.is_override ? 'ring-2 ring-amber-300' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded border flex items-center justify-center overflow-hidden">
                        {amenity.icon_url ? (
                          <img src={amenity.icon_url} alt="" className="w-6 h-6 object-contain" />
                        ) : (
                          <Image className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-800">{amenity.name}</p>
                          {amenity.is_override && (
                            <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                              Overridden
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {amenity.group?.name || 'No group'}
                          {!amenity.is_override && (
                            <span className="text-gray-400"> • Inherited from room type</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {amenity.is_override && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeRoomOverride(amenity.id)}
                          disabled={savingIds.has(amenity.id)}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                          title="Reset to room type setting"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      <Switch
                        checked={amenity.is_active}
                        onCheckedChange={() => toggleRoomAmenity(amenity.id, amenity.is_active, amenity.is_override)}
                        disabled={savingIds.has(amenity.id)}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
